import { List } from 'immutable';

import { getWeightError } from './shipping';
import { setInvalid } from '../variations/errors';
import { getSize } from '../iterable/getSize';
import {
  getBarcodeError,
  getCAPError,
  getCPIError,
  getPriceError,
  getQuantityError,
  getSKUError,
} from './inventory';

import { ALL, ATTRIBUTES_TO_VALIDATE, CHANNEL_VARIANTS, COMBINATIONS, DELETED, ERRORS, INFLUENCE, RETAIN, VALIDATIONS, VARIANTS } from '../../constants/variations';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { EMPTY, VELA } from '../../constants';
import { ERROR } from '../../constants/product';
import ATTRIBUTE from '../../constants/attributes';

export function getCombinationError({ attribute, channel, combinations, variations }) {
  function reduceCombinationsToQuantity(result, combination) {
    return result && (!combination.get(ATTRIBUTE.VISIBILITY) || !parseInt(combination.get(ATTRIBUTE.QUANTITY)));
  }

  function reduceCombinationsToVisibility(result, combination) {
    return result && !combination.get(ATTRIBUTE.VISIBILITY);
  }

  function valueRequired(combination) {
    switch (channel) {
      case ETSY: {
        return combination.get(ATTRIBUTE.VISIBILITY) && getSize(variations.getIn([INFLUENCE, attribute])) > 0;
      }

      case SHOPIFY: {
        if (combination.get(DELETED)) return false;

        switch (attribute) {
          case ATTRIBUTE.QUANTITY: {
            return combination.get(ATTRIBUTE.TRACK_QUANTITY, false);
          }

          case ATTRIBUTE.WEIGHT: {
            return combination.get(ATTRIBUTE.PHYSICAL, false);
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return false;
      }
    }
  }

  function mapCombinationsForBarcodeError(combination) {
    return valueRequired(combination) && getBarcodeError({ channel, barcode: combination.get(ATTRIBUTE.BARCODE) });
  }

  function mapCombinationsForSKUError(combination) {
    return valueRequired(combination) && getSKUError({ channel, sku: combination.get(ATTRIBUTE.SKU) });
  }

  function mapCombinationsForPriceError(combination) {
    return valueRequired(combination) && getPriceError({ channel, price: combination.get(ATTRIBUTE.PRICE) });
  }

  function mapCombinationsForCAPError(combination) {
    return valueRequired(combination) && getCAPError({ channel, cap: combination.get(ATTRIBUTE.CAP) });
  }

  function mapCombinationsForCPIError(combination) {
    return valueRequired(combination) && getCPIError({ channel, cpi: combination.get(ATTRIBUTE.CPI) });
  }

  function mapCombinationsForQuantityError(combination) {
    return (
      valueRequired(combination) &&
      getQuantityError({ channel, quantity: combination.get(ATTRIBUTE.QUANTITY), allowZero: true })
    );
  }

  function mapCombinationsForWeightError(combination) {
    return valueRequired(combination) && getWeightError({ channel, weight: combination.get(ATTRIBUTE.WEIGHT) });
  }

  switch (attribute) {
    case ATTRIBUTE.CAP: {
      if (variations.getIn([RETAIN, ATTRIBUTE.PRICE])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForCAPError);
    }

    case ATTRIBUTE.CPI: {
      if (variations.getIn([RETAIN, ATTRIBUTE.PRICE])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForCPIError);
    }

    case ATTRIBUTE.PRICE: {
      if (variations.getIn([RETAIN, ATTRIBUTE.PRICE])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForPriceError);
    }

    case ATTRIBUTE.QUANTITY: {
      if (variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      switch (channel) {
        case ETSY: {
          if (combinations.reduce(reduceCombinationsToQuantity, true)) {
            return getSize(variations.getIn([INFLUENCE, attribute])) > 0 && ERRORS.COMBINATIONS.NO_STOCK;
          }

          return combinations.map(mapCombinationsForQuantityError);
        }

        default: {
          return combinations.map(mapCombinationsForQuantityError);
        }
      }
    }

    case ATTRIBUTE.SKU: {
      if (variations.getIn([RETAIN, ATTRIBUTE.SKU])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForSKUError);
    }

    case ATTRIBUTE.BARCODE: {
      if (variations.getIn([RETAIN, ATTRIBUTE.BARCODE])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForBarcodeError);
    }

    case ATTRIBUTE.VISIBILITY: {
      return (
        getSize(combinations) > 0 &&
        combinations.reduce(reduceCombinationsToVisibility, true) &&
        ERRORS.COMBINATIONS.NO_VISIBLE
      );
    }

    case ATTRIBUTE.WEIGHT: {
      if (variations.getIn([RETAIN, ATTRIBUTE.SHIPPING])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForWeightError);
    }

    default: {
      return undefined;
    }
  }
}

export function getCombinationsErrors({ channel, variations }) {
  const combinations = variations.get(COMBINATIONS);

  function reduceAttributes(errors, attribute) {
    return errors.set(attribute, getCombinationError({ attribute, channel, combinations, variations }));
  }

  return ATTRIBUTES_TO_VALIDATE[channel].reduce(reduceAttributes, variations.get(ERROR, EMPTY.MAP));
}

export function getOptionNameError({ channel, value }) {
  if (
    VALIDATIONS.OPTIONS.MAXIMUM_NAME_LENGTH.hasOwnProperty(channel) &&
    getSize(value) > VALIDATIONS.OPTIONS.MAXIMUM_NAME_LENGTH[channel]
  ) {
    return ERRORS.OPTIONS.NAME_LENGTH_EXCEEDED[channel];
  }

  switch (channel) {
    case ETSY: {
      return /[\^$`]/.test(value) && ERRORS.OPTIONS.FORBIDDEN_CHARACTERS[channel];
    }

    default: {
      return false;
    }
  }
}

export function getOptionsErrors({ channel, variations }) {
  const errors = variations.get(ERROR, EMPTY.MAP);

  function setOptionErrors(result, variant, variantIndex) {
    function mapOptions(option) {
      return getOptionNameError({ channel, value: option.get('value') });
    }

    return result.set(variantIndex, variant.get('options').map(mapOptions));
  }

  return errors.set('options', variations.get(VARIANTS).reduce(setOptionErrors, errors.get('options', EMPTY.LIST)));
}

export function getVariantNameError({ channel, name, variants, variantIndex }) {
  if (
    VALIDATIONS.VARIANTS.MAXIMUM_NAME_LENGTH.hasOwnProperty(channel) &&
    getSize(name) > VALIDATIONS.VARIANTS.MAXIMUM_NAME_LENGTH[channel]
  ) {
    return ERRORS.VARIANTS.NAME_LENGTH_EXCEEDED[channel];
  }

  function findSameVariant(variant) {
    return variant.get('name') === name && getSize(variant.get('options')) > 0;
  }

  switch (channel) {
    case ETSY: {
      return /[\^$`]/.test(name) && ERRORS.VARIANTS.FORBIDDEN_CHARACTERS[ETSY];
    }

    case SHOPIFY: {
      if (
        !name &&
        variants.getIn([variantIndex, 'name']) !== undefined &&
        getSize(variants.getIn([variantIndex, 'options'])) > 0
      ) {
        return ERRORS.VARIANTS.NAME_REQUIRED;
      } else if (/\s\/\s/.test(name)) {
        return ERRORS.VARIANTS.FORBIDDEN_CHARACTERS[SHOPIFY];
      }

      return (
        variants.delete(variantIndex).findIndex(findSameVariant) !== -1 &&
        ERRORS.VARIANTS.NAME_EXIST
      );
    }

    case VELA: {
      return (
        !name &&
        variants.getIn([variantIndex, 'name']) !== undefined &&
        getSize(variants.getIn([variantIndex, 'options'])) > 0 &&
        ERRORS.VARIANTS.NAME_REQUIRED
      );
    }

    default: {
      return false;
    }
  }
}

export function getVariantsErrors({ /* category, */ channel, variations }) {
  const errors = variations.get(ERROR, EMPTY.MAP);
  const combinationsNumberExceeded = ERRORS.COMBINATIONS.NUMBER_EXCEEDED[channel];
  const optionsNumberExceeded = ERRORS.OPTIONS.NUMBER_EXCEEDED[channel];
  let variantsErrors = errors.get(VARIANTS, EMPTY.MAP);

  function getFileredCombinations(combinations) {
    function filterDeleted(combination) {
      return !combination.get(DELETED);
    }

    switch (channel) {
      case SHOPIFY: {
        return combinations.filter(filterDeleted);
      }

      default: {
        return combinations;
      }
    }
  }

  function getNumberError({ combinations = EMPTY.LIST, options = EMPTY.LIST }) {
    switch (channel) {
      case ETSY: {
        if (getSize(options) > VALIDATIONS.OPTIONS.MAXIMUM_NUMBER[channel]) {
          return optionsNumberExceeded;
        }

        break;
      }

      default: {
        break;
      }
    }

    return (
      getSize(options) > 0 &&
      VALIDATIONS.COMBINATIONS.MAXIMUM_NUMBER.hasOwnProperty(channel) &&
      getSize(getFileredCombinations(combinations)) > VALIDATIONS.COMBINATIONS.MAXIMUM_NUMBER[channel] &&
      combinationsNumberExceeded
    );
  }

  function setNoOptionsError(result, variant, variantIndex) {
    if (getSize(variant.get('options')) === 0) {
      return result.setIn([variantIndex, 'options'], ERRORS.OPTIONS.NO_OPTIONS);
    } else if (result.getIn([variantIndex, 'options']) === ERRORS.OPTIONS.NO_OPTIONS) {
      return result.deleteIn([variantIndex, 'options']);
    } else {
      return result;
    }
  }

  // function setScaleError(source, variant, variantIndex) {
  //   let result = source;
  //   const scale = variant.get('scale');

  //   if (scale === 0) {
  //     result = result.setIn([variantIndex, 'scale'], true);
  //   } else {
  //     const property = variant.get('property');

  //     if (!scale && getSize(getUiState({ channel, propertyId: property, taxonomyId: category }).availableScales) > 0) {
  //       result = result.setIn([variantIndex, 'scale'], true);
  //     } else {
  //       result = result.deleteIn([variantIndex, 'scale']);
  //     }
  //   }

  //   return setNoOptionsError(result, variant, variantIndex);
  // }

  function setOptionsLimitError(result, variant, variantIndex) {
    if (getNumberError({ options: variant.get('options') })) {
      return result.setIn([variantIndex, 'options'], optionsNumberExceeded);
    } else if (
      result.getIn([variantIndex, 'options']) === optionsNumberExceeded ||
      result.getIn([variantIndex, 'options']) === combinationsNumberExceeded
    ) {
      return result.deleteIn([variantIndex, 'options']);
    } else {
      return result;
    }
  }

  function setCombinationsLimitError(result, variant, variantIndex) {
    if (getNumberError({ combinations: variations.get(COMBINATIONS), options: variant.get('options') })) {
      return result.setIn([variantIndex, 'options'], combinationsNumberExceeded);
    } else if (result.getIn([variantIndex, 'options']) === combinationsNumberExceeded) {
      return result.deleteIn([variantIndex, 'options']);
    } else {
      return result;
    }
  }

  switch (channel) {
    case ETSY: {
      // variantsErrors = variations.get(VARIANTS).reduce(setScaleError, variantsErrors);
      variantsErrors = variations.get(VARIANTS).reduce(setNoOptionsError, variantsErrors);

      if (!variations.getIn([INFLUENCE, ALL])) {
        return errors.set(VARIANTS,
          variations.get(VARIANTS).reduce(setOptionsLimitError, variantsErrors)
        );
      }

      break;
    }

    case VELA:
    case SHOPIFY: {
      variantsErrors = variations.get(VARIANTS).reduce(setNoOptionsError, variantsErrors);
      break;
    }

    default: {
      break;
    }
  }

  return errors.set(VARIANTS, variations.get(VARIANTS).reduce(setCombinationsLimitError, variantsErrors));
}

export function updateVariationsErrors({ channel, variations }) {
  const variants = variations.get(VARIANTS);

  function reduceVariants(errors, variantIndex) {
    let error = EMPTY.MAP;
    const variant = variants.get(variantIndex);

    if (variant) {
      error = error.set('name', getVariantNameError({ channel, name: variant.get('name'), variants, variantIndex }));
    }

    return errors.setIn([VARIANTS, variantIndex], error);
  }

  function updateErrors(errors = EMPTY.MAP) {
    return getCombinationsErrors({
      channel,
      variations: variations.set(ERROR, getVariantsErrors({
        channel,
        variations: variations.set(ERROR, getOptionsErrors({
          channel,
          variations: variations.set(ERROR,
            CHANNEL_VARIANTS[channel].reduce(reduceVariants, errors)
          ),
        })),
      })),
    });
  }

  return setInvalid(variations.update(ERROR, updateErrors));
}
